import { BASE_WIDTH } from '@/constants'

const MIN_WIDTH = 320
const MAX_WIDTH = 8000
const MIN_MULTIPLIER = 1.1
const MAX_MULTIPLIER = 1.6

function getDeviceWidth(): number {
  if (typeof window === 'undefined') {
    return BASE_WIDTH
  }
  return window.innerWidth
}

function calculateScaleMultiplier(deviceWidth: number): number {
  const clamped = Math.max(MIN_WIDTH, Math.min(deviceWidth, MAX_WIDTH))
  return MIN_MULTIPLIER + ((clamped - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH)) * (MAX_MULTIPLIER - MIN_MULTIPLIER)
}

function calculateFontSize(baseFontSize: string | number | null | undefined): number | undefined {
  if (baseFontSize == null || baseFontSize === '') {
    return undefined
  }
  const base = typeof baseFontSize === 'number' ? baseFontSize : parseFloat(baseFontSize)
  if (!isFinite(base)) {
    return undefined
  }
  const deviceWidth = getDeviceWidth()
  const scaleMultiplier = calculateScaleMultiplier(deviceWidth)
  let scaledFontSize = (base / BASE_WIDTH) * deviceWidth
  if (deviceWidth < BASE_WIDTH) {
    scaledFontSize = Math.floor(scaledFontSize - 1.5)
  }
  const maxFontSize = base * scaleMultiplier
  return Math.min(scaledFontSize, maxFontSize)
}

export function checkAndApplyFontSize(): void {
  if (typeof document === 'undefined') {
    return
  }
  const isMobile = window.innerWidth <= 768
  if (isMobile) {
    document.body.classList.add('mobile-device')
    document.body.classList.remove('desktop-device')
  } else {
    document.body.classList.add('desktop-device')
    document.body.classList.remove('mobile-device')
  }
}

export function updateFontSizes(html: string): string {
  if (!html) {
    return ''
  }
  return html.replace(/font-size:\s*(\d+(?:\.\d+)?)px;/g, (match, size) => {
    const newSize = calculateFontSize(size)
    return newSize != null ? `font-size: ${newSize}px;` : match
  })
}

export function recalculateFontSizes(): void {
  if (typeof document === 'undefined') {
    return
  }
  document.querySelectorAll<HTMLElement>('[data-fontsize]').forEach(element => {
    const dataFontSize = element.getAttribute('data-fontsize')
    if (!dataFontSize) {
      return
    }
    const newSize = calculateFontSize(dataFontSize)
    if (newSize != null) {
      element.style.fontSize = `${newSize}px`
    }
  })
}

export function applyFont(fontFamily: string): void {
  if (typeof document === 'undefined') {
    return
  }
  document.body.style.fontFamily = fontFamily
}

let resizeTimeout: number | undefined

export function setupFontSizeListeners(): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  checkAndApplyFontSize()
  recalculateFontSizes()

  const handleResize = () => {
    if (resizeTimeout != null) {
      clearTimeout(resizeTimeout)
    }
    resizeTimeout = window.setTimeout(() => {
      checkAndApplyFontSize()
      recalculateFontSizes()
    }, 100)
  }

  window.addEventListener('resize', handleResize)

  return () => {
    window.removeEventListener('resize', handleResize)
    if (resizeTimeout != null) {
      clearTimeout(resizeTimeout)
    }
  }
}
